import axios from 'axios'
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { authApi } from '@/lib/api'
import type { AuthUser, Membership, SessionResponse } from '@/lib/api-types'

const ORG_CACHE_KEY = 'agrosdr.inbox.currentOrgId'
const AUTH_CACHE_KEY = 'agrosdr.inbox.authSnapshot.v1'

interface AuthSnapshot {
  user: AuthUser
  memberships: Membership[]
  currentOrgId: string | null
  cachedAt: string
}

function readCachedOrgId(): string | null {
  try {
    return localStorage.getItem(ORG_CACHE_KEY)
  } catch {
    return null
  }
}

function writeCachedOrgId(id: string | null) {
  try {
    if (id) {
      localStorage.setItem(ORG_CACHE_KEY, id)
    } else {
      localStorage.removeItem(ORG_CACHE_KEY)
    }
  } catch {
    // Private browsing and locked-down storage can throw; auth still works via cookies.
  }
}

function readAuthSnapshot(): AuthSnapshot | null {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AuthSnapshot>
    if (!parsed.user?.id || !Array.isArray(parsed.memberships)) return null
    return {
      user: parsed.user,
      memberships: parsed.memberships,
      currentOrgId: parsed.currentOrgId ?? null,
      cachedAt: parsed.cachedAt ?? new Date(0).toISOString(),
    }
  } catch {
    return null
  }
}

function writeAuthSnapshot(snapshot: Omit<AuthSnapshot, 'cachedAt'> | null) {
  try {
    if (!snapshot) {
      localStorage.removeItem(AUTH_CACHE_KEY)
      return
    }
    localStorage.setItem(
      AUTH_CACHE_KEY,
      JSON.stringify({ ...snapshot, cachedAt: new Date().toISOString() })
    )
  } catch {
    // Storage can fail in private browsing; cookies still keep live auth working.
  }
}

function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  return axios.isAxiosError(error) && !error.response
}

export interface AuthContextValue {
  user: AuthUser | null
  memberships: Membership[]
  currentOrgId: string | null
  currentRole: Membership['role'] | null
  loading: boolean
  usingCachedSession: boolean
  refresh: () => Promise<void>
  switchOrg: (orgId: string) => void
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
)

async function fetchSession(): Promise<SessionResponse> {
  const { data } = await authApi.get<SessionResponse>('/api/auth/get-session')
  return data
}

async function fetchMemberships(): Promise<Membership[]> {
  const { data } = await authApi.get<Membership[]>('/v1/users/me/memberships')
  return data ?? []
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(
    readCachedOrgId
  )
  const [loading, setLoading] = useState(true)
  const [usingCachedSession, setUsingCachedSession] = useState(false)

  const applySnapshot = useCallback((snapshot: AuthSnapshot) => {
    setUser(snapshot.user)
    setMemberships(snapshot.memberships)

    const cachedOrgId = readCachedOrgId() ?? snapshot.currentOrgId
    const validCached = snapshot.memberships.find(
      (membership) => membership.organizationId === cachedOrgId
    )
    const resolvedOrgId =
      validCached?.organizationId ??
      snapshot.memberships[0]?.organizationId ??
      null

    setCurrentOrgId(resolvedOrgId)
    writeCachedOrgId(resolvedOrgId)
    setUsingCachedSession(true)
  }, [])

  const clearAuthState = useCallback(() => {
    setUser(null)
    setMemberships([])
    setCurrentOrgId(null)
    setUsingCachedSession(false)
    writeCachedOrgId(null)
    writeAuthSnapshot(null)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)

    try {
      const session = await fetchSession()

      if (!session.user) {
        clearAuthState()
        return
      }

      setUser(session.user)

      let nextMemberships: Membership[]
      let loadedFromCache = false
      try {
        nextMemberships = await fetchMemberships()
      } catch (error) {
        const snapshot = readAuthSnapshot()
        if (
          isNetworkFailure(error) &&
          snapshot?.user.id === session.user.id &&
          snapshot.memberships.length > 0
        ) {
          nextMemberships = snapshot.memberships
          loadedFromCache = true
        } else {
          nextMemberships = []
        }
      }
      setMemberships(nextMemberships)

      const cachedOrgId = readCachedOrgId()
      const validCached = nextMemberships.find(
        (membership) => membership.organizationId === cachedOrgId
      )
      const resolvedOrgId =
        validCached?.organizationId ??
        nextMemberships[0]?.organizationId ??
        null

      setCurrentOrgId(resolvedOrgId)
      writeCachedOrgId(resolvedOrgId)
      writeAuthSnapshot({
        user: session.user,
        memberships: nextMemberships,
        currentOrgId: resolvedOrgId,
      })
      setUsingCachedSession(loadedFromCache)
    } catch (error) {
      const snapshot = readAuthSnapshot()
      if (snapshot && isNetworkFailure(error)) {
        applySnapshot(snapshot)
      } else {
        clearAuthState()
      }
    } finally {
      setLoading(false)
    }
  }, [applySnapshot, clearAuthState])

  useEffect(() => {
    void load()
  }, [load])

  const refresh = useCallback(async () => {
    await load()
  }, [load])

  const switchOrg = useCallback((orgId: string) => {
    setCurrentOrgId(orgId)
    writeCachedOrgId(orgId)
    const snapshot = readAuthSnapshot()
    if (snapshot) {
      writeAuthSnapshot({ ...snapshot, currentOrgId: orgId })
    }
  }, [])

  const signOut = useCallback(async () => {
    await authApi.post('/api/auth/sign-out')
    clearAuthState()
  }, [clearAuthState])

  const currentRole =
    memberships.find((membership) => membership.organizationId === currentOrgId)
      ?.role ?? null

  return (
    <AuthContext.Provider
      value={{
        user,
        memberships,
        currentOrgId,
        currentRole,
        loading,
        usingCachedSession,
        refresh,
        switchOrg,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
